import { create } from 'zustand'

import { IconNameType } from '@ds'

import { createSelectors } from '../helpers'
import { StoreItem } from '../shop'
import { QuestHint } from '../quest'

interface OverlayPayloadData {
  type:
    | 'dialog'
    | 'chapter'
    | 'narrator'
    | 'achievement'
    | 'info'
    | 'error'
    | 'soon'
    | 'item'
    | 'battlePass'
    | 'termsAndPrivacy'
    | 'acceptPrivacy'
    | 'transactionProcessing'
    | 'payment'
    | 'donate'
    | 'donateReward'
    | 'refill'
    | 'openNewQuest'
    | 'currencyReward'
    | 'hintPayment'
  payload?: Record<string, any>
}

export interface AcceptPrivacyOverlayData extends OverlayPayloadData {
  type: 'acceptPrivacy'
}

export interface TermsAndPrivacyOverlayData extends OverlayPayloadData {
  type: 'termsAndPrivacy'
  payload: {
    type: 'terms' | 'privacy'
  }
}

export interface TransactionProcessingOverlayData extends OverlayPayloadData {
  type: 'transactionProcessing'
}

export interface DialogOverlayData extends OverlayPayloadData {
  type: 'dialog'
  payload: {
    slug: string
  }
}

export interface ChapterOverlayData extends OverlayPayloadData {
  type: 'chapter'
  payload: {
    title: string
    text: string
    onClose: () => void
    soundPhrase?: string
  }
}

export interface NarratorOverlayData extends OverlayPayloadData {
  type: 'narrator'
  payload: {
    text: string
    onClose: () => void
    soundPhrase?: string
  }
}

export interface AchievementOverlayData extends OverlayPayloadData {
  type: 'achievement'
  payload: {
    isAnimationImg: boolean
    title: string
    description: string
    image: string
  }
}

export interface InfoOverlayData extends OverlayPayloadData {
  type: 'info'
  payload: {
    title: string
    description?: string
    image: string
    icon: IconNameType
    buttonText: string
    hideCloseButton?: boolean
    onClose?: () => void
  }
}

export interface ErrorOverlayData extends OverlayPayloadData {
  type: 'error'
  payload: {
    description: string
    onClose?: () => void
  }
}

export interface ItemOverlayData extends OverlayPayloadData {
  type: 'item'
  payload: {
    title: string
    description: string
    image: string
    rewardType?: string
    onClose?: () => void
  }
}
export interface SoonOverlayData extends OverlayPayloadData {
  type: 'soon'
}

export interface BattlePassOverlayData extends OverlayPayloadData {
  type: 'battlePass'
  payload: {
    title: string
    description: string
    image: string
    rewardType?: string
    onClose?: () => void
  }
}

export interface PaymentOverlayData extends OverlayPayloadData {
  type: 'payment'
  payload: {
    item: StoreItem
  }
}

export interface DonateOverlayData extends OverlayPayloadData {
  type: 'donate'
}

export interface DonateRewardOverlayData extends OverlayPayloadData {
  type: 'donateReward'
  payload: {
    onConfirm: () => Promise<void>
  }
}

export interface HintPaymentOverlayData extends OverlayPayloadData {
  type: 'hintPayment'
  payload: {
    hint: QuestHint
  }
}

export interface RefillOverlayData extends OverlayPayloadData {
  type: 'refill'
}

export interface CurrencyRewardOverlayData extends OverlayPayloadData {
  type: 'currencyReward'
  payload: {
    amount: number
    description: string
    onClaim?: () => void
  }
}

export interface OpenNewQuestOverlayData extends OverlayPayloadData {
  type: 'openNewQuest'
  payload: {
    questSlug: string
  }
}

export type OverlayPayload =
  | DialogOverlayData
  | ChapterOverlayData
  | NarratorOverlayData
  | AchievementOverlayData
  | BattlePassOverlayData
  | InfoOverlayData
  | ErrorOverlayData
  | ItemOverlayData
  | SoonOverlayData
  | TermsAndPrivacyOverlayData
  | AcceptPrivacyOverlayData
  | TransactionProcessingOverlayData
  | PaymentOverlayData
  | DonateOverlayData
  | DonateRewardOverlayData
  | RefillOverlayData
  | OpenNewQuestOverlayData
  | CurrencyRewardOverlayData
  | HintPaymentOverlayData

interface OverlayStore {
  isLoading: boolean
  overlay: OverlayPayload | null
  queue: OverlayPayload[]
  afterDialogComplete: (() => void) | null
  previousOverlay: OverlayPayload | null
  actions: {
    setOverlayQueue: (items: OverlayPayload[]) => void
    hideOverlay: () => void
    setAfterDialogComplete: (callback: () => void) => void
  }
}

export const useOverlayStoreBase = create<OverlayStore>((set) => ({
  isLoading: false,
  overlay: null,
  queue: [],
  afterDialogComplete: null,
  previousOverlay: null,
  actions: {
    setAfterDialogComplete: (callback: () => void) => {
      set({ afterDialogComplete: callback })
    },
    setOverlayQueue: (items) => {
      if (!items || items.length === 0) return

      set((state) => {
        const currentOverlay = state.overlay
        const currentQueue = state.queue

        if (currentOverlay) {
          return { queue: [...currentQueue, ...items] }
        } else {
          const [first, ...rest] = items
          return {
            overlay: first,
            queue: [...currentQueue, ...rest],
            previousOverlay: first,
          }
        }
      })
    },
    hideOverlay: () => {
      set((state) => {
        const [next, ...rest] = state.queue
        if (state.previousOverlay && state.previousOverlay.type === 'dialog') {
          //TODO: fix this timeout
          setTimeout(() => {
            state.afterDialogComplete?.()
            set({ afterDialogComplete: null })
          })
        }
        if (next) {
          return { overlay: next, queue: rest, previousOverlay: next }
        } else {
          return { overlay: null, previousOverlay: null }
        }
      })
    },
  },
}))

export const useOverlayActions = () =>
  useOverlayStoreBase((state) => state.actions)

export const useOverlay = createSelectors(useOverlayStoreBase)
